import React, { useEffect, useState } from 'react';
import StableMuiDialog from '../../../common/StableMuiDialog.js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';

const colors = {
  bgPrimary: '#ffffff',
  textPrimary: '#1a1a18',
  textSecondary: '#5f5e5a',
  textTertiary: '#888780',
  borderTertiary: 'rgba(0,0,0,0.12)',
  borderSecondary: 'rgba(0,0,0,0.22)',
  accent: '#185FA5',
  accentBg: '#E6F1FB',
  accentBorder: '#B5D4F4',
};

const MethodCard = ({ active, badge, icon, name, description, onClick }) => (
  <Box
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    }}
    sx={{
      border: '1.5px solid',
      borderColor: active ? colors.accent : colors.borderTertiary,
      borderRadius: '8px',
      p: '12px 14px',
      cursor: 'pointer',
      position: 'relative',
      transition: 'border-color 0.15s, background 0.15s',
      background: active ? colors.accentBg : colors.bgPrimary,
      '&:hover': { borderColor: active ? colors.accent : colors.borderSecondary },
    }}
  >
    {badge && (
      <Box
        component="span"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          fontSize: 10,
          fontWeight: 500,
          px: '7px',
          py: '2px',
          borderRadius: '20px',
          border: '0.5px solid',
          background: colors.accentBg,
          color: colors.accent,
          borderColor: colors.accentBorder,
        }}
      >
        {badge}
      </Box>
    )}
    <Box sx={{ fontSize: 20, color: active ? colors.accent : colors.textSecondary, mb: 0.75 }}>
      {icon}
    </Box>
    <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary, m: '0 0 2px' }}>
      {name}
    </Typography>
    <Typography sx={{ fontSize: 11, color: colors.textSecondary, m: 0, lineHeight: 1.4 }}>
      {description}
    </Typography>
  </Box>
);

/** Internal send-settlements prompt: optional stripped XLS on the email. */
const IncludeSettlementXlsDialog = ({ open, onClose, onConfirm }) => {
  const [includeXls, setIncludeXls] = useState(false);

  useEffect(() => {
    if (open) setIncludeXls(false);
  }, [open]);

  return (
    <StableMuiDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: 460,
          borderRadius: '16px',
          border: `0.5px solid ${colors.borderSecondary}`,
          bgcolor: colors.bgPrimary,
          m: 2,
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography
          component="h2"
          sx={{ fontSize: 17, fontWeight: 500, color: colors.textPrimary, m: '0 0 4px' }}
        >
          Send settlements
        </Typography>
        <Typography sx={{ fontSize: 13, color: colors.textSecondary, m: '0 0 20px', lineHeight: 1.45 }}>
          "Include spreadsheet" is an optional convenience for the town.
        </Typography>

        <Typography
          component="p"
          sx={{
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: colors.textTertiary,
            m: '0 0 8px',
          }}
        >
          Attachment
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2.5 }}>
          <MethodCard
            active={!includeXls}
            badge="Default"
            icon={<MailOutlineIcon fontSize="inherit" />}
            name="Stipulation PDFs"
            description="Send the settlement email without a spreadsheet"
            onClick={() => setIncludeXls(false)}
          />
          <MethodCard
            active={includeXls}
            icon={<TableChartOutlinedIcon fontSize="inherit" />}
            name="Include spreadsheet"
            description="Also attach the settled-case list for the town"
            onClick={() => setIncludeXls(true)}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, pt: 0.5 }}>
          <Button
            onClick={onClose}
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: colors.textSecondary,
              border: `0.5px solid ${colors.borderSecondary}`,
              borderRadius: '8px',
              px: 2,
              py: 1,
              textTransform: 'none',
              minWidth: 'auto',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(includeXls)}
            variant="contained"
            disableElevation
            sx={{
              fontSize: 13,
              fontWeight: 500,
              bgcolor: colors.accent,
              borderRadius: '8px',
              px: 2.5,
              py: 1,
              textTransform: 'none',
              minWidth: 'auto',
              '&:hover': { bgcolor: '#144f8a' },
            }}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </StableMuiDialog>
  );
};

export default IncludeSettlementXlsDialog;
